import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  GoogleMap,
  InfoWindow,
  useJsApiLoader,
  Marker,
} from "@react-google-maps/api";
import { MarkerF as AdvancedMarker } from "@react-google-maps/api";
import { animateLocation, cleanupLocationScrollTriggers } from "./gsap";
import {
  BUSINESS_NAME,
  BUSINESS_SLOGAN,
  BUSINESS_ADDRESS,
  BUSINESS_HOURS,
} from "@/core/constants/business";

const containerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "320px",
  borderRadius: "0.5rem",
};

const mapStyles = [
  {
    featureType: "poi.business",
    elementType: "labels",
    stylers: [{ visibility: "on" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#e9e9e9" }, { lightness: 17 }],
  },
  {
    featureType: "landscape",
    elementType: "geometry",
    stylers: [{ color: "#f5f5f5" }, { lightness: 20 }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }, { lightness: 17 }],
  },
];

// Extract address from business constants and create location object
const location = {
  address: BUSINESS_ADDRESS,
  lat: 38.9332,
  lng: -77.058606,
};

// Format business hours for display
const formatBusinessHours = () => {
  if (typeof BUSINESS_HOURS.monday === "string") {
    return BUSINESS_HOURS.monday;
  }

  return `${BUSINESS_HOURS.monday.open.replace(
    /^(\d+):00/,
    "$1"
  )}am - ${BUSINESS_HOURS.monday.close.replace(/^(\d+):00/, "$1")}pm`;
};

const LocationSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const formattedHours = formatBusinessHours();

  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBFUIzxEwVOnNgLtjKbUi2sLhdw0EbdPjw",
  });

  useEffect(() => {
    // Apply animations after a small delay to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (
        sectionRef.current &&
        contentRef.current &&
        cardRef.current &&
        mapRef.current
      ) {
        animateLocation(
          { current: contentRef.current },
          { current: cardRef.current },
          { current: sectionRef.current },
          { current: mapRef.current }
        );
      }
    }, 100);

    // Clean up on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupLocationScrollTriggers();
    };
  }, []);

  // Open info window by default when map loads
  const onMapLoad = React.useCallback(() => {
    setInfoOpen(true);
  }, []);

  return (
    <section ref={sectionRef} className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div ref={contentRef} className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Visit Our Dispensary
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            Experience premium cannabis products at our DC location. We're now
            open and ready to serve you!
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Google Map */}
          <div
            ref={mapRef}
            className="w-full lg:w-2/3 h-80 rounded-lg shadow-lg overflow-hidden relative"
            style={{ minHeight: "320px" }}
          >
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={{ lat: location.lat, lng: location.lng }}
                zoom={16}
                options={{
                  styles: mapStyles,
                  disableDefaultUI: true,
                  zoomControl: true,
                }}
                onLoad={onMapLoad}
              >
                <Marker
                  position={{ lat: location.lat, lng: location.lng }}
                  title={BUSINESS_NAME}
                  onClick={() => setInfoOpen(true)}
                />

                {infoOpen && (
                  <InfoWindow
                    position={{ lat: location.lat, lng: location.lng }}
                    onCloseClick={() => setInfoOpen(false)}
                  >
                    <div style={{ padding: "10px" }}>
                      <h4 style={{ margin: 0, fontWeight: "bold" }}>
                        {BUSINESS_NAME}
                      </h4>
                      <p style={{ margin: 0 }}>{BUSINESS_ADDRESS}</p>
                      <p style={{ margin: 0 }}>Open Daily: {formattedHours}</p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-cannabis-green-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          {/* Location Card */}
          <div
            ref={cardRef}
            className="w-full lg:w-1/3 bg-gray-100 p-8 rounded-lg shadow-md"
          >
            <h3 className="text-2xl font-semibold mb-4">DC Location</h3>
            <p className="text-gray-700 font-medium mb-4">{BUSINESS_ADDRESS}</p>
            <p className="text-gray-700 font-medium mb-6">
              Open Daily: {formattedHours}
            </p>
            <div className="space-y-3">
              <Link
                to="/locations"
                className="block w-full text-center px-6 py-3 bg-cannabis-green-500 hover:bg-cannabis-green-600 text-white font-medium rounded-lg transition duration-300"
              >
                Full Location Details
              </Link>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  BUSINESS_ADDRESS
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-6 py-3 border-2 border-cannabis-green-500 text-cannabis-green-600 hover:bg-cannabis-green-50 font-medium rounded-lg transition duration-300"
              >
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;
